import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useLanguage } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, ArrowRight, Loader2, CheckCircle } from "lucide-react";

export default function ForgotPassword() {
  const { t, language, toggleLanguage } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // We intentionally always show success to prevent enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-yellow-500 rounded-2xl flex items-center justify-center"><span className="text-black font-bold text-xl">س</span></div>
          </Link>
          <button onClick={toggleLanguage} className="text-xs text-slate-400 hover:text-yellow-500 absolute top-4 left-4">{language === "ar" ? "EN" : "AR"}</button>
        </div>

        <div className="glass-card rounded-3xl p-8" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="text-green-400" size={32} />
              </div>
              <h1 className="text-2xl font-bold text-white">{t("افحص بريدك", "Check your email")}</h1>
              <p className="text-slate-300">{t(
                "إذا كان البريد مسجلاً لدينا، فقد أرسلنا لك رابط إعادة تعيين كلمة المرور. الرابط صالح لمدة ساعة.",
                "If your email is registered, we sent you a reset link. It is valid for 1 hour."
              )}</p>
              <p className="text-slate-500 text-sm">{t("لم يصل بريد؟ تحقق من مجلد الرسائل غير المرغوبة.", "No email? Check your spam folder.")}</p>
              <Link to="/login" className="block btn-primary text-center py-3 rounded-xl mt-4" data-testid="back-to-login">
                {t("العودة لتسجيل الدخول", "Back to login")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h1 className="text-2xl font-bold text-luxury mb-2">{t("نسيت كلمة المرور؟", "Forgot password?")}</h1>
                <p className="text-slate-400 text-sm">{t(
                  "أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين.",
                  "Enter your email and we'll send you a reset link."
                )}</p>
              </div>
              <div>
                <label className="text-sm text-slate-300 block mb-2">{t("البريد الإلكتروني", "Email")}</label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-yellow-500" size={16} />
                  <Input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com" required
                    className="bg-slate-900 border-slate-700 text-white pr-10 pl-3 py-6 rounded-xl"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="btn-primary w-full h-12 rounded-xl" data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : t("إرسال الرابط", "Send Reset Link")}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-yellow-500 hover:underline">
                <ArrowRight size={14} />
                {t("العودة لتسجيل الدخول", "Back to login")}
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
